import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import {
  Crown,
  FileSignature,
  Search,
  Landmark,
  TrendingUp,
  ClipboardList,
  FileText,
  Users,
  Calculator,
  BookOpen,
  Send,
  Plus,
  Loader2,
  Trash2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  Crown, FileSignature, Search, Landmark, TrendingUp, ClipboardList,
  FileText, Users, Calculator, BookOpen,
};

const SUGGESTED = {
  executive_advisor: [
    "Draft a board-level talking points memo on our Q1 strategy.",
    "What are the top 3 risks for our AgriSat engagement?",
    "Frame our position vs. McKinsey on a national mandate.",
  ],
  proposal_generator: [
    "Draft a PPP engagement letter for the Ministry of Food and Agriculture.",
    "Generate an investor memo for Sovereign Wealth Partners (GH₵ 130M).",
    "Create a consulting proposal for Greater Accra regional economic diagnostic.",
  ],
  research_analyst: [
    "Summarize 2026 trends in West African agricultural policy.",
    "Competitive landscape: top African advisory firms.",
    "Map regulatory risk for sovereign-backed PPP deals.",
  ],
  government_strategist: [
    "Stakeholder map for the Office of the Vice President.",
    "Brief on protocol for a ministerial-level briefing.",
    "Talking points for a national PPP advocacy meeting.",
  ],
  investor_advisor: [
    "Talking points for our next investor briefing.",
    "Term-sheet positioning for a GH₵ 180M growth round.",
    "Draft an IR update following our Accra engagement.",
  ],
  operations_planner: [
    "Convert the AgriSat mandate into a 12-week workplan.",
    "Identify dependencies and risks for our PPP framework.",
    "Build a deliverable matrix for the Q1 IR series.",
  ],
  reporting_assistant: [
    "Write a 1-page executive memo on this week's engagements.",
    "Draft an action-item-only meeting summary template.",
    "Generate a board update format we can reuse.",
  ],
  crm_assistant: [
    "Draft a follow-up to Marcus Aldridge after our last call.",
    "Strategy for moving Atlas Capital from qualified to engaged.",
    "Suggest a relationship-scoring rubric for government contacts.",
  ],
  financial_intelligence: [
    "Build a 3-scenario forecast for the AgriSat mandate.",
    "Sensitivity analysis on FX risk in our pipeline.",
    "Translate our pipeline into recognized vs. probable revenue.",
  ],
  knowledge_synthesizer: [
    "Synthesize our internal policy memos into an executive brief.",
    "Pull out the top 5 insights across our research repository.",
    "Compare AgriSat memo against the WAEC policy review.",
  ],
};

export default function AICommandCenter() {
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [agentSearch, setAgentSearch] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get("/ai/agents").then((r) => {
      setAgents(r.data);
      if (r.data?.[0]) setAgentId(r.data[0].id);
    });
    refreshConvs();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const refreshConvs = async () => {
    const r = await api.get("/ai/conversations");
    setConversations(r.data);
  };

  const openConv = async (c) => {
    setConvId(c.id);
    setAgentId(c.agent_id);
    const r = await api.get(`/ai/conversations/${c.id}/messages`);
    setMessages(r.data);
  };

  const newChat = () => {
    setConvId(null);
    setMessages([]);
  };

  const deleteConv = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/ai/conversations/${id}`);
    if (id === convId) newChat();
    refreshConvs();
  };

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || !agentId) return;
    setInput("");
    const optimistic = {
      id: `tmp-${Date.now()}`, role: "user", content: message,
      agent_id: agentId, created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    setBusy(true);
    try {
      const r = await api.post("/ai/chat", { agent_id: agentId, message, conversation_id: convId });
      setConvId(r.data.conversation_id);
      setMessages((m) => [...m, r.data.message]);
      refreshConvs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI request failed");
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
    } finally {
      setBusy(false);
    }
  };

  const activeAgent = useMemo(() => agents.find((a) => a.id === agentId), [agents, agentId]);
  const filteredAgents = agents.filter((a) =>
    !agentSearch || a.name.toLowerCase().includes(agentSearch.toLowerCase())
  );

  return (
    <div className="h-[calc(100vh-58px)] flex" data-testid="ai-page">
      {/* Agent rail */}
      <div className="w-[280px] shrink-0 border-r flex flex-col" style={{ borderColor: "var(--border-default)", background: "var(--bg-surface-1)" }}>
        <div className="p-4 border-b" style={{ borderColor: "var(--border-default)" }}>
          <div className="label-eyebrow mb-2">// AI Agents</div>
          <div className="font-display text-lg text-white">The Brain</div>
          <input
            data-testid="agent-search"
            value={agentSearch}
            onChange={(e) => setAgentSearch(e.target.value)}
            placeholder="Search agents…"
            className="mt-3 w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-xs placeholder:text-slate-600 focus:border-[#D4AF37] focus:outline-none"
          />
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {filteredAgents.map((a) => {
            const Icon = ICONS[a.icon] || Sparkles;
            const active = a.id === agentId;
            return (
              <button
                key={a.id}
                data-testid={`agent-${a.id}`}
                onClick={() => { setAgentId(a.id); newChat(); }}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-all border-l-2 ${active ? "bg-[#17223B] text-white" : "text-slate-400 hover:bg-[#10182B] hover:text-white border-transparent"}`}
                style={{ borderLeftColor: active ? "var(--gold-500)" : "transparent" }}
              >
                <Icon className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} style={{ color: active ? "var(--gold-400)" : undefined }} />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{a.name.replace("Nexus ", "")}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">{a.tagline}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Conversations rail */}
      <div className="w-[240px] shrink-0 border-r flex flex-col" style={{ borderColor: "var(--border-default)", background: "var(--bg-surface-1)" }}>
        <div className="p-4 flex items-center justify-between border-b" style={{ borderColor: "var(--border-default)" }}>
          <div className="label-eyebrow">// Sessions</div>
          <button
            data-testid="new-chat-btn"
            onClick={newChat}
            className="p-1.5 border border-white/10 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] text-slate-400 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {conversations.length === 0 && (
            <div className="text-xs text-slate-600 font-mono p-4">No sessions yet.</div>
          )}
          {conversations.map((c) => {
            const a = agents.find((x) => x.id === c.agent_id);
            const Icon = ICONS[a?.icon] || Sparkles;
            return (
              <div
                key={c.id}
                onClick={() => openConv(c)}
                data-testid={`conv-${c.id}`}
                className={`group p-2.5 cursor-pointer border transition-all ${convId === c.id ? "border-[#D4AF37]/50 bg-[#17223B]" : "border-transparent hover:border-white/10 hover:bg-[#10182B]"}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5 text-slate-500 shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white truncate">{c.title}</div>
                    <div className="text-[10px] text-slate-500 font-mono mt-0.5 truncate">
                      {a?.name.replace("Nexus ", "")}
                    </div>
                  </div>
                  <button
                    onClick={(e) => deleteConv(c.id, e)}
                    className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-all"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Chat */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        {activeAgent && (
          <div className="px-8 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border-default)" }}>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center border border-[#D4AF37]/40" style={{ background: "rgba(212,175,55,0.08)" }}>
                {(() => { const Icon = ICONS[activeAgent.icon] || Sparkles; return <Icon className="w-5 h-5" style={{ color: "var(--gold-400)" }} strokeWidth={1.5} />; })()}
              </div>
              <div>
                <div className="font-display text-lg text-white">{activeAgent.name}</div>
                <div className="text-xs text-slate-400">{activeAgent.tagline}</div>
              </div>
            </div>
            <div className="font-mono text-[10px] tracking-widest text-slate-500">CLAUDE SONNET 4.5 // SECURE</div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-8 py-6 nexus-topo">
          {messages.length === 0 && (
            <div className="max-w-2xl mx-auto pt-16">
              <div className="label-eyebrow mb-3">// Empty session</div>
              <h2 className="font-display text-3xl text-white mb-3">Brief the {activeAgent?.name.replace("Nexus ", "")}</h2>
              <p className="text-slate-400 text-sm mb-8">
                Start a new institutional intelligence session. Conversations are persisted to your audit-logged archive.
              </p>
              <div className="space-y-2">
                <div className="label-eyebrow mb-2">// Suggested prompts</div>
                {(SUGGESTED[agentId] || []).map((s, i) => (
                  <button
                    key={i}
                    data-testid={`suggested-${i}`}
                    onClick={() => send(s)}
                    className="block w-full text-left p-4 border border-white/10 hover:border-[#D4AF37]/40 text-slate-300 hover:text-white transition-all text-sm"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className={`max-w-3xl mb-6 ${m.role === "user" ? "ml-auto" : ""}`}
            >
              {m.role === "user" ? (
                <div className="bg-[#10182B] border border-white/10 p-4 text-white text-sm whitespace-pre-wrap">
                  {m.content}
                </div>
              ) : (
                <div className="border-l-2 pl-5 py-1" style={{ borderColor: "var(--gold-500)" }}>
                  <div className="label-eyebrow mb-2">// NEXUS RESPONSE</div>
                  <div className="text-slate-200 text-sm whitespace-pre-wrap leading-relaxed nexus-prose">
                    {m.content}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
          {busy && (
            <div className="max-w-3xl border-l-2 pl-5 py-1" style={{ borderColor: "var(--gold-500)" }}>
              <div className="label-eyebrow mb-2">// THINKING</div>
              <div className="flex items-center gap-1">
                <span className="nexus-dot w-1.5 h-1.5 bg-[#D4AF37] rounded-full"></span>
                <span className="nexus-dot w-1.5 h-1.5 bg-[#D4AF37] rounded-full"></span>
                <span className="nexus-dot w-1.5 h-1.5 bg-[#D4AF37] rounded-full"></span>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Composer */}
        <div className="border-t p-4" style={{ borderColor: "var(--border-default)", background: "var(--bg-surface-1)" }}>
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="max-w-4xl mx-auto flex items-end gap-3"
          >
            <div className="flex-1 relative">
              <span className="absolute left-3 top-3 font-mono text-xs text-[#D4AF37]">{`>`}</span>
              <textarea
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder={`Command the ${activeAgent?.name.replace("Nexus ", "") || "agent"}…`}
                rows={2}
                className="w-full pl-9 pr-4 py-3 bg-[#050B14] border border-white/10 text-white font-mono text-sm resize-none focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/30"
              />
            </div>
            <button
              type="submit"
              disabled={busy || !input.trim()}
              data-testid="ai-send-btn"
              className="px-5 py-3 font-medium disabled:opacity-40 transition-all flex items-center gap-2"
              style={{ background: "var(--gold-500)", color: "#050B14" }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              EXECUTE
            </button>
          </form>
          <div className="max-w-4xl mx-auto mt-2 font-mono text-[10px] text-slate-600 tracking-widest">
            ENTER TO SEND // SHIFT + ENTER FOR NEW LINE
          </div>
        </div>
      </div>
    </div>
  );
}
